/**
 * KinsCircle Phase 6C — Relationship-aware historian engine.
 *
 * STABILITY.md §13:
 *   - ADVISORY only.
 *   - NEVER fabricates facts (no inferred deaths, illnesses, crimes, awards,
 *     military service, etc.).
 *   - NEVER invents relationships not already present in the data.
 *   - Every output anchors back to existing memory IDs via `sourceMemoryIds`.
 */

import {
  isHistorianModeEnabled,
  isRelationshipInsightsEnabled,
} from './semanticFeatureFlags';
import {
  readIndexedMemories,
  buildMemoryClusters,
} from './semanticIndexer';
import {
  extractRelationshipSignals,
  type RelationshipInsight,
} from './memoryEmbeddingAdapter';

export type FamilyEraSummary = {
  era: string;
  yearRange: { from: number; to: number };
  count: number;
  themes: string[];
  topPeople: string[];
  sourceMemoryIds: string[];
};

export type RelationshipTimelineEntry = {
  memberKey: string;
  firstYear: number | null;
  lastYear: number | null;
  appearances: number;
  themes: string[];
  sourceMemoryIds: string[];
};

export type LifeMoment = {
  label: string;
  memoryId: string;
  year: number | null;
  caption: string;
  taggedNames: string[];
};

export type RepeatedTradition = {
  label: string;
  occurrences: number;
  yearRange: { from: number | null; to: number | null };
  sourceMemoryIds: string[];
};

export type CrossGenerationConnection = {
  memberKey: string;
  generationsSpanned: number;
  yearRange: { from: number; to: number };
  sourceMemoryIds: string[];
};

const MILESTONE_KEYWORDS: { label: string; pattern: RegExp }[] = [
  { label: 'Wedding', pattern: /\b(wedding|married|wed|nuptials)\b/i },
  { label: 'Graduation', pattern: /\b(graduation|graduated|diploma|commencement)\b/i },
  { label: 'New Baby', pattern: /\b(newborn|baby|birth|christening|baptism)\b/i },
  { label: 'Anniversary', pattern: /\b(anniversary)\b/i },
  { label: 'Reunion', pattern: /\b(reunion|family gathering)\b/i },
  { label: 'Big Birthday', pattern: /\b(50th|60th|70th|80th|90th|100th)\b/i },
  { label: 'Homecoming', pattern: /\b(homecoming|prom)\b/i },
];

const TRADITION_KEYWORDS = [
  'christmas',
  'thanksgiving',
  'easter',
  'hanukkah',
  'diwali',
  'ramadan',
  'cookout',
  'barbecue',
  'reunion',
  'fourth of july',
  'new year',
  'birthday',
];

const eraLabel = (yearMid: number): string => {
  if (yearMid < 1960) return 'The Early Years';
  if (yearMid < 1980) return 'The 60s & 70s';
  if (yearMid < 1995) return 'The 80s & Early 90s';
  if (yearMid < 2010) return 'The Late 90s & 2000s';
  if (yearMid < 2020) return 'The 2010s';
  return 'Recent Years';
};

/**
 * Group all indexed memories into broad eras (10–20 year buckets) and
 * surface the dominant themes + most-tagged people inside each.
 */
export const generateFamilyEraSummary = (): FamilyEraSummary[] => {
  if (!isHistorianModeEnabled()) return [];
  const all = readIndexedMemories();
  if (!all.length) return [];

  type Bucket = {
    yearMid: number;
    rows: typeof all;
  };
  const buckets: Record<string, Bucket> = {};

  for (const item of all) {
    if (!item.vector.year) continue;
    // Decade-snap.
    const decade = Math.floor(item.vector.year / 10) * 10;
    const key = String(decade);
    if (!buckets[key]) buckets[key] = { yearMid: decade + 4, rows: [] };
    buckets[key].rows.push(item);
  }

  return Object.keys(buckets)
    .sort()
    .map((k) => {
      const b = buckets[k];
      const years = b.rows
        .map((r) => r.vector.year)
        .filter((y): y is number => typeof y === 'number');
      const themeCounts: Record<string, number> = {};
      const peopleCounts: Record<string, number> = {};
      for (const r of b.rows) {
        for (const t of Object.keys(r.vector.terms)) {
          if (t.startsWith('@')) continue;
          themeCounts[t] = (themeCounts[t] ?? 0) + r.vector.terms[t];
        }
        for (const p of r.vector.people) {
          peopleCounts[p] = (peopleCounts[p] ?? 0) + 1;
        }
      }
      const themes = Object.keys(themeCounts)
        .sort((a, b1) => themeCounts[b1] - themeCounts[a])
        .slice(0, 4);
      const topPeople = Object.keys(peopleCounts)
        .sort((a, b1) => peopleCounts[b1] - peopleCounts[a])
        .slice(0, 4);
      return {
        era: eraLabel(b.yearMid),
        yearRange: {
          from: years.length ? Math.min(...years) : b.yearMid - 4,
          to: years.length ? Math.max(...years) : b.yearMid + 5,
        },
        count: b.rows.length,
        themes,
        topPeople,
        sourceMemoryIds: b.rows.map((r) => r.row.id).slice(0, 12),
      };
    })
    .filter((era) => era.count >= 2);
};

/**
 * Per-relative timeline: when they first appear, when they last appear,
 * how often, and the dominant themes around them. Pure aggregation —
 * no fact invention.
 */
export const buildRelationshipTimeline = (): RelationshipTimelineEntry[] => {
  if (!isRelationshipInsightsEnabled()) return [];
  const all = readIndexedMemories();
  if (!all.length) return [];

  const signals: RelationshipInsight[] = extractRelationshipSignals(all, {
    minAppearances: 2,
  });

  return signals.map((s) => {
    const sourceMemoryIds = all
      .filter((m) => m.vector.people.includes(s.memberKey))
      .map((m) => m.row.id)
      .slice(0, 10);
    return {
      memberKey: s.memberKey,
      firstYear: s.firstYear,
      lastYear: s.lastYear,
      appearances: s.appearances,
      themes: s.topTerms,
      sourceMemoryIds,
    };
  });
};

/**
 * Surface 6–10 "biggest moments" — wedding, graduation, new baby, etc.
 * Anchored to specific memories. Skips anything with fabrication risk
 * (no death/illness/crime detection).
 */
export const findMajorLifeMoments = (limit = 10): LifeMoment[] => {
  if (!isHistorianModeEnabled()) return [];
  const all = readIndexedMemories();
  if (!all.length) return [];

  const out: LifeMoment[] = [];
  for (const { row, vector } of all) {
    const text = `${row.title ?? ''} ${row.caption ?? ''}`;
    for (const m of MILESTONE_KEYWORDS) {
      if (m.pattern.test(text)) {
        out.push({
          label: m.label,
          memoryId: row.id,
          year: vector.year,
          caption: (row.caption ?? '').slice(0, 200),
          taggedNames: row.taggedNames ?? [],
        });
        break;
      }
    }
  }

  return out
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
    .slice(0, Math.max(1, Math.min(20, limit)));
};

/**
 * Detect repeated themes that appear in 3+ years (Christmas across
 * multiple decades, the annual cookout, etc.).
 */
export const findRepeatedFamilyTraditions = (): RepeatedTradition[] => {
  if (!isHistorianModeEnabled()) return [];
  const all = readIndexedMemories();
  if (!all.length) return [];

  const out: RepeatedTradition[] = [];
  for (const keyword of TRADITION_KEYWORDS) {
    const re = new RegExp(`\\b${keyword.replace(/\s+/g, '\\s+')}\\b`, 'i');
    const hits = all.filter(({ row }) =>
      re.test(`${row.title ?? ''} ${row.caption ?? ''}`),
    );
    if (hits.length < 3) continue;
    const years = hits
      .map((h) => h.vector.year)
      .filter((y): y is number => typeof y === 'number');
    const distinctYears = new Set(years);
    if (distinctYears.size < 2) continue;

    out.push({
      label: keyword
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
      occurrences: hits.length,
      yearRange: {
        from: years.length ? Math.min(...years) : null,
        to: years.length ? Math.max(...years) : null,
      },
      sourceMemoryIds: hits.map((h) => h.row.id).slice(0, 12),
    });
  }
  return out.sort((a, b) => b.occurrences - a.occurrences);
};

/**
 * Find members tagged across memories spanning 25+ years — a soft proxy
 * for "appears across multiple generations of family history." Pure
 * counting; no biological inference.
 */
export const findCrossGenerationConnections = (): CrossGenerationConnection[] => {
  if (!isRelationshipInsightsEnabled()) return [];
  const all = readIndexedMemories();
  if (!all.length) return [];

  const byPerson: Record<
    string,
    { years: number[]; ids: string[] }
  > = {};
  for (const { row, vector } of all) {
    if (!vector.year) continue;
    for (const p of vector.people) {
      if (!byPerson[p]) byPerson[p] = { years: [], ids: [] };
      byPerson[p].years.push(vector.year);
      byPerson[p].ids.push(row.id);
    }
  }

  return Object.keys(byPerson)
    .map((p) => {
      const ys = byPerson[p].years;
      const from = Math.min(...ys);
      const to = Math.max(...ys);
      const span = to - from;
      // 25-year span ≈ at least one generation apart.
      const generationsSpanned =
        span >= 50 ? 3 : span >= 25 ? 2 : 1;
      return {
        memberKey: p,
        generationsSpanned,
        yearRange: { from, to },
        sourceMemoryIds: byPerson[p].ids.slice(0, 10),
      };
    })
    .filter((c) => c.generationsSpanned >= 2)
    .sort(
      (a, b) =>
        b.generationsSpanned - a.generationsSpanned ||
        b.yearRange.to - b.yearRange.from -
          (a.yearRange.to - a.yearRange.from),
    );
};

/**
 * Convenience: build a full historian dashboard payload in one call.
 */
export const buildHistorianDashboard = (): {
  eras: FamilyEraSummary[];
  traditions: RepeatedTradition[];
  moments: LifeMoment[];
  timelines: RelationshipTimelineEntry[];
  crossGen: CrossGenerationConnection[];
  clusters: ReturnType<typeof buildMemoryClusters>;
} => {
  return {
    eras: generateFamilyEraSummary(),
    traditions: findRepeatedFamilyTraditions(),
    moments: findMajorLifeMoments(),
    timelines: buildRelationshipTimeline(),
    crossGen: findCrossGenerationConnections(),
    clusters: buildMemoryClusters(),
  };
};
